import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Bell, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { invoicesService } from '@/features/invoices/services/invoices.service';
import {
  parsePage,
  parseReferenceMonth,
  parseSearchText,
} from '@/features/invoices/invoice-search';
import { MonthSelect } from '@/features/invoices/components/MonthSelect';
import { BulkReminderDialog } from '@/features/invoices/components/BulkReminderDialog';
import { formatMonthCapitalized } from '@/features/invoices/components/invoice-formatters';
import { useSendInvoiceReminder } from '@/features/invoices/hooks/useSendInvoiceReminder';
import { useDebouncedValue } from '@/features/invoices/hooks/useDebouncedValue';

const PAGE_SIZE = 20;

interface InvoiceMissingSearch {
  referenceMonth?: string;
  page?: number;
  search?: string;
}

export const Route = createFileRoute('/_authenticated/admin/invoices/missing')({
  validateSearch: (search: Record<string, unknown>): InvoiceMissingSearch => ({
    referenceMonth: parseReferenceMonth(search.referenceMonth),
    page: parsePage(search.page),
    search: parseSearchText(search.search),
  }),
  component: AdminInvoicesMissingPage,
});

function AdminInvoicesMissingPage() {
  const { referenceMonth, page = 1, search } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/invoices/missing' });
  const { toast } = useToast();
  const [searchInput, setSearchInput] = useState(search ?? '');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const debouncedSearch = useDebouncedValue(searchInput);

  useEffect(() => {
    const next = debouncedSearch.trim() || undefined;
    if (next !== (search ?? undefined)) {
      navigate({ search: (prev) => ({ ...prev, search: next, page: undefined }), replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Mês efetivo: o da URL ou o padrão do backend (próximo a faturar).
  const { data: stats } = useQuery({
    queryKey: ['invoice-stats', referenceMonth || 'open'],
    queryFn: () => invoicesService.getStats(referenceMonth),
    placeholderData: (prev) => prev,
  });
  const activeMonth = referenceMonth ?? stats?.referenceMonth ?? '';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['invoice-providers', 'missing', activeMonth, page, search ?? ''],
    queryFn: () =>
      invoicesService.getProviders({
        referenceMonth: activeMonth,
        filter: 'missing',
        page,
        limit: PAGE_SIZE,
        search,
      }),
    enabled: !!activeMonth,
    placeholderData: (prev) => prev,
  });

  const sendReminder = useSendInvoiceReminder();
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  /** Count sem filtro de busca — o alerta em massa envia para todos os faltantes. */
  const bulkMissingCount =
    stats?.referenceMonth === activeMonth ? (stats.missingCount ?? 0) : 0;

  const handleBulk = async () => {
    if (!activeMonth) return;
    setBulkBusy(true);
    let all: Awaited<ReturnType<typeof invoicesService.getAllMissingProviders>>;
    try {
      all = await invoicesService.getAllMissingProviders(activeMonth);
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os prestadores sem nota.',
        variant: 'destructive',
      });
      setBulkBusy(false);
      return;
    }

    try {
      if (all.length === 0) {
        toast({ title: 'Nenhum alerta enviado', description: 'Nenhum prestador sem nota.' });
      } else {
        // O toast de sucesso (inclui skippedCount) e o de erro são tratados por useSendInvoiceReminder.
        await sendReminder.mutateAsync({
          userIds: all.map((p) => p.userId),
          referenceMonth: activeMonth,
        });
      }
      setBulkOpen(false);
    } catch {
      // erro de envio já exibido pelo hook; mantém o diálogo aberto para nova tentativa
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1">
            <Link to="/admin/invoices" search={activeMonth ? { referenceMonth: activeMonth } : {}}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Gestão de Notas
            </Link>
          </Button>
          <h1 className="text-3xl font-bold">Notas Faltantes</h1>
          <p className="text-muted-foreground">
            Prestadores PJ sem nota em {activeMonth ? formatMonthCapitalized(activeMonth) : '...'}
          </p>
        </div>
        <MonthSelect
          value={activeMonth}
          onChange={(month) =>
            navigate({ search: (prev) => ({ ...prev, referenceMonth: month, page: undefined }) })
          }
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Prestadores sem Nota do Período
              </CardTitle>
              <CardDescription>
                {search
                  ? `${total} resultado(s) na busca · ${bulkMissingCount} faltante(s) no total`
                  : `${bulkMissingCount || total} prestador(es) ainda não enviaram a nota desta competência`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar nome, e-mail ou CNPJ"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-[260px] pl-9"
                />
              </div>
              <Button
                variant="outline"
                disabled={
                  !activeMonth || bulkMissingCount === 0 || bulkBusy || sendReminder.isPending
                }
                onClick={() => setBulkOpen(true)}
              >
                <Bell className="mr-2 h-4 w-4" />
                Enviar alerta para todos
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && !data ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : isError && !data ? (
            <div className="text-center py-8 space-y-3 text-muted-foreground">
              <p>Não foi possível carregar a lista.</p>
              <Button variant="outline" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>
                {search
                  ? 'Nenhum prestador encontrado para a busca.'
                  : 'Todos os prestadores enviaram a nota deste período.'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((provider) => (
                    <TableRow key={provider.userId}>
                      <TableCell className="font-medium">
                        {provider.fullName || provider.email}
                      </TableCell>
                      <TableCell>{provider.email}</TableCell>
                      <TableCell>{provider.cnpj || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!activeMonth || sendReminder.isPending}
                          onClick={() =>
                            sendReminder.mutate({
                              userIds: [provider.userId],
                              referenceMonth: activeMonth,
                            })
                          }
                        >
                          <Bell className="mr-1 h-4 w-4" />
                          Enviar alerta
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                total={total}
                page={page}
                limit={PAGE_SIZE}
                onPageChange={(next) =>
                  navigate({ search: (prev) => ({ ...prev, page: next > 1 ? next : undefined }) })
                }
              />
            </>
          )}
        </CardContent>
      </Card>

      <BulkReminderDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        referenceMonth={activeMonth}
        missingCount={bulkMissingCount}
        isPending={bulkBusy || sendReminder.isPending}
        onConfirm={() => void handleBulk()}
      />
    </div>
  );
}
